import { useCallback, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { uploadFile, compareFileExtension } from 'utils/helpers'
import { Button } from 'components/common'
import { CloseIcon, CsvFileIcon, UploadIcon } from 'components/icons'

import './fileUpload.css'

type FileUploadButtonProps = {
  confirmButtonText?: string
  uploadButtonText?: string
  supportedFileTypes?: string
  uploadApiFunction: (data: any) => Promise<any>
  setRefetch?: React.Dispatch<React.SetStateAction<boolean>>
}

/**
 * ### Will render a upload button with uploading file in chunks functionality and confirm button for *file upload with progress bar
 */
const FileUploadButton = ({
  confirmButtonText = 'Upload & Save',
  uploadButtonText = 'File',
  supportedFileTypes = '.csv',
  uploadApiFunction,
  setRefetch,
}: FileUploadButtonProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [progress, setProgress] = useState(0)

  const inputRef = useRef<HTMLInputElement>(null)

  // To handle input change
  const handleFileChange = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    // Will check if file exists
    if (target.files && target.files.length > 0) {
      // Will check if the extension of file matches the required extension
      if (compareFileExtension(target?.files[0], 'csv')) {
        setSelectedFile(target.files[0])
      } else {
        toast.error('Invalid File Format')
      }
    }
  }

  //  To handle reset
  const resetFileUpload = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = ''
      setSelectedFile(null)
      setProgress(0)
    }
  }, [])

  // To submit file upload
  const handleFileUpload = async () => {
    const toastId = toast.loading('Uploading file', { duration: Infinity })
    await uploadFile(selectedFile, uploadApiFunction, {
      setProgress,
      toastId,
      onError: resetFileUpload,
      onComplete: () => {
        toast.success('File Uploaded Successfully', {
          duration: 5000,
          id: toastId,
        })
        resetFileUpload()
        setRefetch?.((pre) => !pre)
      },
    })
  }

  // Will be triggered on button click to explicitly trigger file input tag
  const handleSelectFile = () => {
    inputRef.current?.click()
  }

  // Will render the buttons Based on the progress
  const renderActionButton = useMemo(() => {
    if (progress === 0 || progress === 100) {
      return (
        <Button
          buttonText={confirmButtonText}
          onClick={handleFileUpload}
          disabled={!selectedFile}
          className="w-40"
        />
      )
    } else {
      return (
        <div
          style={{ '--statusWidth': `${progress}%` } as any}
          className={`status relative flex w-40 items-center justify-center overflow-hidden rounded-md bg-gray-300 after:absolute after:left-0 after:h-full after:bg-black`}
        >
          <div className={`z-10  font-medium text-white mix-blend-difference`}>
            {`${progress}% uploaded`}
          </div>
        </div>
      )
    }
  }, [progress, selectedFile])

  return (
    <div className="flex flex-wrap gap-5">
      <input
        className="hidden"
        type="file"
        onChange={handleFileChange}
        accept={supportedFileTypes}
        ref={inputRef}
      />
      {selectedFile ? (
        <Button
          buttonText={selectedFile.name}
          onClick={resetFileUpload}
          theme={'success'}
          className={`w-40`}
          icon={<CsvFileIcon />}
          iconClassName="!h-5 !w-5"
          textClassName="flex-1"
          icon2={progress > 0 && progress < 100 ? null : <CloseIcon />}
          icon2ClassName="!h-3 !w-3 text-[#161C28]"
          disabled={progress > 0 && progress < 100}
        />
      ) : (
        <Button
          buttonText={uploadButtonText}
          onClick={handleSelectFile}
          theme={'primary'}
          className={`w-40`}
          icon={<UploadIcon />}
          iconClassName="!h-5 !w-5"
        />
      )}
      {renderActionButton}
    </div>
  )
}

export default FileUploadButton
